import { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';

import { BodyText, Button, Modal, theme, useResponsive } from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationRequiredModificationModal = (props: Props) => {
  const { isVisible, productName, requiredIngredientName, onConfirm, onShow, size } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const ingredientName = (
    requiredIngredientName ?? formatMessage(messages.ingredient)
  ).toLowerCase();

  // ─── Styles ──────────────────────────────────────────────────────────

  const ctaStyles = size === 'lg' ? styles.ctaLG : styles.ctaXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onConfirm}
    >
      <CustomizationModalContentContainer size={size ?? match(['md', 'lg'])}>
        <BodyText sizeMatch={['22']}>
          <FormattedMessage
            {...messages.body}
            values={{ ingredient: ingredientName, product: productName }}
          />
        </BodyText>

        <View style={styles.ctaContainer}>
          <Button size="large-wide" palette="spinachMuted" style={ctaStyles} onPress={onConfirm}>
            <FormattedMessage {...messages.gotIt} />
          </Button>
        </View>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalLabel: {
    defaultMessage: 'Required ingredient modal',
    description: 'Customization | Required modification modal | Label',
  },
  body: {
    defaultMessage: 'This {ingredient} cannot be removed from the {product}.',
    description: 'Customization | Required modification modal | Body',
  },
  ingredient: {
    defaultMessage: 'ingredient',
    description: 'Customization | Required modification modal | Ingredient',
  },
  product: {
    defaultMessage: 'product',
    description: 'Customization | Required modification modal | Product',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Customization | Required modification modal | Got it button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['2'],
    paddingBottom: Platform.select({
      android: theme.spacing['2'],
      default: 0,
    }),
  },
  ctaXS: {
    paddingHorizontal: theme.spacing['16'],
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
  ctaLG: {
    minHeight: 72,
    paddingHorizontal: theme.spacing['16'],
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  productName: string;
  requiredIngredientName: string | undefined;
  onConfirm: () => void;
  onShow?: () => void;
  size?: 'md' | 'lg';
};
